import React, { useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>{children}</div>;
}

const EMPTY = {
  name: "", job_title: "", description: "", openings: 0, schedule: "", work_type: "",
  requirements: "", background_check_required: false, equipment_required: false,
  start_date: "", end_date: "", client_offered_pay_rate: "", client_offered_training_rate: "",
  pay_type: "Hourly", payment_frequency: "Weekly",
  training_requirements: { training_start_date: "", training_duration: "", training_schedule: "", training_format: "" },
};
const TR_FIELDS = [["training_start_date", "Training start date", "date"], ["training_duration", "Training length", "text"], ["training_schedule", "Training schedule", "text"], ["training_format", "Format (virtual / on-site)", "text"]];
const EDITABLE = ["Draft", "Pending Review", "Approved", "Paused"];

export default function ClientPrograms() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/client/programs").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] ?? EMPTY[k]])), training_requirements: { ...EMPTY.training_requirements, ...(p.training_requirements || {}) } });
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, openings: Number(form.openings) || 0,
        start_date: form.start_date || null, end_date: form.end_date || null,
        client_offered_pay_rate: form.client_offered_pay_rate === "" || form.client_offered_pay_rate == null ? null : Number(form.client_offered_pay_rate),
        client_offered_training_rate: form.client_offered_training_rate === "" || form.client_offered_training_rate == null ? null : Number(form.client_offered_training_rate) };
      if (editId) await api.put(`/client/programs/${editId}`, payload);
      else await api.post("/client/programs", payload);
      toast({ title: editId ? "Program updated" : "Program created", description: editId ? "Your changes were saved." : "Saved as Draft. Submit it for Campbell review when ready." });
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast({ title: "Failed", description: errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const submit = async (p) => {
    try { await api.post(`/client/programs/${p.id}/submit`); toast({ title: "Submitted for review" }); load(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toFixed(2)}`);

  return (
    <div className="space-y-4" data-testid="client-programs">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">My Programs</h2>
          <p className="text-sm text-slate-500">Create and submit hiring programs. Campbell reviews and publishes them.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="btn-primary-gradient border-0 text-white" data-testid="new-program-btn">New program</Button>
      </div>

      {rows.length === 0 && <Card><p className="text-sm text-slate-400">No programs yet. Create your first one.</p></Card>}

      {rows.map((p) => (
        <Card key={p.id} data-testid={`client-program-${p.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-bold text-slate-900">{p.name}</div>
              <div className="text-sm text-slate-500">{p.job_title} · {p.work_type || "—"} · {p.openings} openings</div>
              <div className="mt-1 inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700" data-testid={`client-program-status-${p.id}`}>{p.status}</div>
              <div className="mt-2 text-sm text-slate-600">Your offer: <strong>{money(p.client_offered_pay_rate)}</strong> · training {money(p.client_offered_training_rate)}</div>
            </div>
            <div className="flex gap-2">
              {EDITABLE.includes(p.status) && <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-program-${p.id}`}>Edit</Button>}
              {(p.status === "Draft") && <Button size="sm" onClick={() => submit(p)} data-testid={`submit-program-${p.id}`}>Submit for review</Button>}
            </div>
          </div>
        </Card>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit program" : "New program"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Program name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Job title</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="pf-title" /></div>
              <div className="space-y-1.5"><Label>Openings</Label><Input type="number" value={form.openings} onChange={(e) => setForm({ ...form, openings: e.target.value })} data-testid="pf-openings" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pf-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Work type</Label><Input value={form.work_type} onChange={(e) => setForm({ ...form, work_type: e.target.value })} placeholder="Full-Time / Part-Time" data-testid="pf-worktype" /></div>
              <div className="space-y-1.5"><Label>Schedule</Label><Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} data-testid="pf-schedule" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Offered pay ($)</Label><Input type="number" value={form.client_offered_pay_rate} onChange={(e) => setForm({ ...form, client_offered_pay_rate: e.target.value })} data-testid="pf-pay" /></div>
              <div className="space-y-1.5"><Label>Offered training pay ($)</Label><Input type="number" value={form.client_offered_training_rate} onChange={(e) => setForm({ ...form, client_offered_training_rate: e.target.value })} data-testid="pf-trainpay" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="pf-start" /></div>
              <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="pf-end" /></div>
            </div>
            <div className="rounded-lg border border-slate-100 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Training requirements &amp; scheduling</div>
              <div className="grid grid-cols-2 gap-3">
                {TR_FIELDS.map(([k, label, type]) => (
                  <div key={k} className="space-y-1.5"><Label>{label}</Label><Input type={type} value={form.training_requirements?.[k] || ""} onChange={(e) => setForm({ ...form, training_requirements: { ...form.training_requirements, [k]: e.target.value } })} data-testid={`pf-${k}`} /></div>
                ))}
              </div>
            </div>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.background_check_required} onChange={(e) => setForm({ ...form, background_check_required: e.target.checked })} data-testid="pf-bg" /> Background check required</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.equipment_required} onChange={(e) => setForm({ ...form, equipment_required: e.target.checked })} data-testid="pf-equip" /> Equipment required</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.name || busy} className="btn-primary-gradient border-0 text-white" data-testid="save-program-btn">{busy ? "Saving…" : editId ? "Save changes" : "Create program"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
